import { useEffect, useRef } from "react";
import type { SWRHook, SWRResponse } from "swr";
import { RESULT_TYPE, ResponseType } from "../../types/swr";
import { getEnvValueFor } from "../utils";
import { appEventsMessaging } from "../hooks/pubSub";

// Define types for the SWR middleware parameters
type SWRKey = string | null | undefined;
type SWRFetcher<Data = any> = (...args: any[]) => Promise<Data>;
type SWRConfig = Record<string, any>;

// Define type for the result wait promise resolve/reject functions
type ResultWaitResolve<T = any> = (value: T | PromiseLike<T>) => void;
type ResultWaitReject = (reason?: any) => void;

// Define type for the pending result data
interface PendingResultData {
  result_id: string;
  errorred?: boolean;
  // When the result is small enough, the backend includes it inline in the push message.
  // If present AND contains result_meta, the frontend can use it directly (no 2nd HTTP request).
  result?: any;
}

const offThreadQueryResponse = (useSWRNext: SWRHook) => {
  return (key: SWRKey, fetcher: SWRFetcher, config: SWRConfig) => {
    const pendingResultUrl = useRef<string | null>(null);
    const pendingResultId = useRef<string | null>(null);
    const pendingResultWaitSub = useRef<any>(null);

    const getThresholdValueFromEnv = (): number => {
      let offThreadQueryTimeout =
        getEnvValueFor("GLOBAL_OFF_THREAD_QUERY_TIMEOUT") || "100";
      let offThreadQueryWaitThreshold =
        getEnvValueFor("OFF_THREAD_QUERY_WAIT_PERIOD_THRESHOLD") || "20";
      let refetchTimeout =
        (parseInt(offThreadQueryTimeout) +
          parseInt(offThreadQueryWaitThreshold)) *
        1000;
      return refetchTimeout;
    };
    const resultWaitTimeoutValue = useRef<number>(getThresholdValueFromEnv());
    const resultWaitTimer = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      if (pendingResultUrl.current !== key) {
        // Whenever the request url is changed, clean up subscriptions
        if (pendingResultWaitSub.current) {
          pendingResultWaitSub.current.unsubscribe();
        }
        if (pendingResultUrl.current && resultWaitTimer.current) {
          clearTimeout(resultWaitTimer.current);
        }
        // Update the pendingResultUrl ref to the current key
        pendingResultUrl.current = key || null;
      }
      return () => {
        // Clean up, unsubscribe events to prevent memory leaks.
        if (pendingResultWaitSub.current) {
          pendingResultWaitSub.current.unsubscribe();
        }
        if (resultWaitTimer.current) {
          clearTimeout(resultWaitTimer.current);
        }
      };
    }, [key]);

    let resultWaitPromiseResolve: ResultWaitResolve;
    let resultWaitPromiseReject: ResultWaitReject;

    const onResultWaitSub = async (data: PendingResultData): Promise<void> => {
      if (pendingResultId.current === data.result_id) {
        if (resultWaitTimer.current) {
          clearTimeout(resultWaitTimer.current);
        }
        if (data.errorred) {
          console.error(`Request failed from server for pending result ${data?.result_id}`);
          resultWaitPromiseReject(new Error('Request Failed. Please try again'));
        } else if (data.result && data.result.result_meta) {
          // Result was included inline in the push message (small enough to fit).
          // Use it directly without making a 2nd HTTP request.
          console.debug(`[SWR MIDDLEWARE] Using inline push result for ${data.result_id}`);
          resultWaitPromiseResolve(data.result);
        } else {
          // Fall back to 2nd HTTP request to fetch transformed result from server
          try {
            const separator = key?.includes('?') ? '&' : '?';
            const currentPendingResultUrl = `${key}${separator}pending_result_id=${pendingResultId.current}`;
            const res = await fetcher(currentPendingResultUrl);
            resultWaitPromiseResolve(res);
          } catch (e) {
            resultWaitPromiseReject(e);
          }
        }
      }
    };

    const modifiedFetcher = async (...args: any[]): Promise<any> => {
      const resultWaitPromise = new Promise<any>((resolve, reject) => {
        resultWaitPromiseResolve = resolve;
        resultWaitPromiseReject = reject;
      });
      const response = await fetcher(...args) as ResponseType;
      if (response.result_meta) {
        let resultType = response.result_meta.result_type;
        if (resultType === RESULT_TYPE.PENDING) {
          pendingResultId.current = response.result_meta.result_id || null;
          pendingResultWaitSub.current = appEventsMessaging(
            `pending_result_ready_${response.result_meta.result_id}`,
            { onSubscribe: onResultWaitSub, once: true }
          );
          pendingResultWaitSub.current.subscribe();
          resultWaitTimer.current = setTimeout(() => {
            if (pendingResultWaitSub.current) {
              pendingResultWaitSub.current.unsubscribe();
            }
            console.error(
              `Request timed out from client for pending result id ${response.result_meta.result_id}`
            );
            resultWaitPromiseReject(
              new Error(`Result wait timeout after ${resultWaitTimeoutValue.current / 1000}s`)
            );
          }, resultWaitTimeoutValue.current);
          return await resultWaitPromise;
        }
        if (
          [
            RESULT_TYPE.ERROR,
            RESULT_TYPE.BLOCKED,
            RESULT_TYPE.TIMEOUT,
          ].includes(resultType)
        ) {
          throw new Error("Request failed. Please try again");
        }
      }
      return response;
    };
    const swr = useSWRNext(key, modifiedFetcher, config) as SWRResponse<any, any>;
    return swr;
  };
};

export default offThreadQueryResponse;
